import * as _ from 'lodash';
import {
  CarrierHierarchyDto,
  CarrierUnitDto,
  CompleteCourierRegistrationDto,
  CourierApprovalDto,
  CourierDto,
  CreateCourierDto,
  EditCourierDto,
  KnownCourierAppRoles,
  KnownCourierApprovalStatus,
  KnownCourierLoginMethod,
  KnownUserStatus
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { FormConfigBase } from '../../../core/forms/form-config';
import { mobilePhoneValidator } from '../../../ui/util/mobile-phone.validator';
import { accessCodeValidator } from '../../../ui/util/access-code.validator';
import { REJECTED_BADGE_STATUS } from '../../../ui/components/pure/user-status-badge/badge-user-status';

/** Which credential the courier signs in with, and so which Login details tab is being filled in. */
export type LoginTab = 'mobile' | 'externalID' | 'accessCode';

/** The courier fields shared by creating and editing one. */
export interface CourierFormState {
  firstName: string;
  lastName?: string;
  mobile?: string;
  externalID?: string;
  username?: string;
  accessCode?: string;
}

/**
 * Create additionally picks the group the courier joins, and can leave a note. Edit does neither.
 *
 * One group, not a carrier and a child of it: the hierarchy endpoint already narrows the units to
 * what this caller can write to, so there is nothing left to narrow by picking a carrier first.
 */
export interface CreateCourierFormState extends CourierFormState {
  groupID: string;
  notes?: string;
}

/** A move is only ever a change of destination — none of the courier's own fields are in play. */
export interface MoveCourierFormState {
  groupID: string;
}

/** The mobile is already set, and the API always sets the status to pending. */
export interface CompleteRegistrationFormState {
  groupID: string;
  firstName: string;
  lastName?: string;
}

// Names are sent on to AC, which rejects these.
const AC_FORBIDDEN_NAME_CHARACTERS = /["\\]/;
export const COURIER_NAME_MAX_LENGTH = 100;

/** Blank passes; whether a name is required is up to the field. */
export function courierNameValidator(name?: string): true | string {
  if (!name) {
    return true;
  }
  if (name.length > COURIER_NAME_MAX_LENGTH) {
    return `Use ${COURIER_NAME_MAX_LENGTH} characters or fewer.`;
  }
  if (AC_FORBIDDEN_NAME_CHARACTERS.test(name)) {
    return 'Names cannot contain " or \\.';
  }
  return true;
}

export function makeCompleteRegistrationFormConfig(): FormConfigBase<CompleteRegistrationFormState> {
  return {
    groupID: {
      name: 'groupID',
      label: 'Carrier',
      required: true
    },
    firstName: {
      name: 'firstName',
      label: 'First name',
      required: true,
      validate: (value) => {
        const name = value?.trim();
        return name ? courierNameValidator(name) : 'Enter a first name.';
      }
    },
    lastName: {
      name: 'lastName',
      label: 'Last name',
      required: false,
      validate: (value) => courierNameValidator(value?.trim())
    }
  };
}

export function mapCompleteRegistrationFormToDto(form: CompleteRegistrationFormState): CompleteCourierRegistrationDto {
  const lastName = form.lastName?.trim();

  return {
    groupID: form.groupID,
    firstName: form.firstName.trim(),
    ...(lastName ? { lastName } : {})
  };
}

/** By access level too: a courier deactivated before finishing is inactive but still incomplete. */
export function isCourierIncomplete(courier: Pick<CourierDto, 'status' | 'accessLevel'>): boolean {
  return (
    courier.accessLevel === KnownCourierAppRoles.CourierIncomplete || courier.status === KnownUserStatus.Incomplete
  );
}

/** The destination unit; the hierarchy already narrows the options, so only the current one is ruled out. */
export function makeMoveCourierFormConfig(currentGroupID?: string): FormConfigBase<MoveCourierFormState> {
  return {
    groupID: {
      name: 'groupID',
      label: 'Move to',
      required: true,
      validate: (groupID) =>
        !groupID || groupID !== currentGroupID || 'This courier is already in this group. Choose another.'
    }
  };
}

/**
 * The tab that goes with a carrier's login method — the credential that carrier issues by default.
 * Anything unrecognised falls back to mobile, which is how couriers sign in by default.
 */
export function loginTabForMethod(loginMethod?: string): LoginTab {
  switch (loginMethod) {
    case KnownCourierLoginMethod.Id:
      return 'externalID';
    case KnownCourierLoginMethod.AccessCode:
      return 'accessCode';
    default:
      return 'mobile';
  }
}

/** The login methods as they read in the UI. */
export const loginTabLabels: Record<LoginTab, string> = {
  mobile: 'Mobile Number',
  externalID: 'ID/Username',
  accessCode: 'ID/Username and Access Code'
};

/** How a courier signs in, and whether that is established fact or their carrier's intention. */
export type CourierLogin = {
  tab: LoginTab;
  label: string;
  /** True when the courier holds nothing, so this is their carrier's default rather than fact. */
  isCarrierDefault: boolean;
};

/**
 * How an existing courier signs in, from what they hold — `loginMethod` is the carrier's default and
 * sign-in never reads it. So the carrier's method leads only where the courier holds the credential
 * it names; otherwise what they hold decides, a mobile last since it may be contact detail alone.
 */
export function describeCourierLogin(
  courier: Pick<CourierDto, 'mobile' | 'externalID' | 'username' | 'loginMethod'>
): CourierLogin {
  // A credential of nothing but whitespace is not one, whatever the record says.
  const holds = (credential?: string) => !!credential?.trim();

  // The username is what says a courier signs in with a code — it is the half that identifies
  // them, and the API will not let one be set without the other.
  const held: Record<LoginTab, boolean> = {
    accessCode: holds(courier.username),
    externalID: holds(courier.externalID),
    mobile: holds(courier.mobile)
  };

  const carrierTab = loginTabForMethod(courier.loginMethod);
  const tab = pickLoginTab(held, carrierTab, !!courier.loginMethod);

  return {
    tab,
    label: loginTabLabels[tab],
    isCarrierDefault: !held.accessCode && !held.externalID && !held.mobile
  };
}

function pickLoginTab(held: Record<LoginTab, boolean>, carrierTab: LoginTab, carrierHasMethod: boolean): LoginTab {
  if (carrierHasMethod && held[carrierTab]) {
    return carrierTab;
  }

  if (held.accessCode) {
    return 'accessCode';
  }

  if (held.externalID) {
    return 'externalID';
  }

  if (held.mobile) {
    return 'mobile';
  }

  // Nothing held to go on, so fall back to whatever their carrier issues.
  return carrierTab;
}

/** The tab an existing courier's edit form opens on — see `describeCourierLogin`. */
export function inferLoginTab(
  courier: Pick<CourierDto, 'mobile' | 'externalID' | 'username' | 'loginMethod'>
): LoginTab {
  return describeCourierLogin(courier).tab;
}

/**
 * Mobile, externalID, and username/access code each belong to their own Login details tab and
 * are only required — and only validated — while that tab is the one being filled out.
 */
export function makeCourierFormConfig(activeLoginTab: LoginTab): FormConfigBase<CourierFormState> {
  return {
    firstName: {
      name: 'firstName',
      label: 'First name',
      required: true,
      validate: (value) => !_.isEmpty(value)
    },
    lastName: {
      name: 'lastName',
      label: 'Last name',
      required: false
    },
    mobile: {
      name: 'mobile',
      label: 'Mobile number',
      required: activeLoginTab === 'mobile',
      // Only the active tab is validated: the others are hidden with `display: none`, not
      // unmounted, so a part-typed number would block the save with an error nobody can see.
      validate: (mobile) => activeLoginTab !== 'mobile' || !mobile || mobilePhoneValidator(mobile)
    },
    externalID: {
      name: 'externalID',
      label: 'ID/username',
      required: activeLoginTab === 'externalID'
    },
    username: {
      name: 'username',
      label: 'Username',
      required: activeLoginTab === 'accessCode'
    },
    accessCode: {
      name: 'accessCode',
      label: 'Access code',
      required: activeLoginTab === 'accessCode',
      // Only the active tab is validated, for the same reason the mobile field is: a hidden tab's
      // half-typed code would block the save with an error nobody can see.
      validate: (accessCode) => activeLoginTab !== 'accessCode' || !accessCode || accessCodeValidator(accessCode)
    }
  };
}

export function makeCreateCourierFormConfig(activeLoginTab: LoginTab): FormConfigBase<CreateCourierFormState> {
  return {
    ...makeCourierFormConfig(activeLoginTab),
    groupID: {
      name: 'groupID',
      label: 'Group',
      required: true
    },
    notes: {
      name: 'notes',
      label: 'Optional notes',
      required: false
    }
  };
}

/**
 * Where a new courier goes, and whether to ask.
 *
 * `defaultUnitID` leads: it is the unit the project being viewed runs, which is where a courier
 * added from that project belongs. A region has one; a company runs regions rather than a unit of
 * its own and has none, and then the organisation is the carrier and its container is submitted for
 * the API to resolve the unit beneath it. A lone operating unit is still never offered as a choice.
 */
export function resolveCourierDestination(hierarchy?: CarrierHierarchyDto): {
  /** The group to submit, or the one to preselect when the picker is shown. */
  groupID?: string;
  showUnitPicker: boolean;
} {
  const units = hierarchy?.units ?? [];
  const showUnitPicker = units.length > 1;

  if (hierarchy?.defaultUnitID) {
    return { groupID: hierarchy.defaultUnitID, showUnitPicker };
  }

  if (showUnitPicker) {
    return { showUnitPicker };
  }

  return {
    groupID: hierarchy?.organisation?.id ?? units[0]?.id,
    showUnitPicker
  };
}

/**
 * With no company the hierarchy is a network's, which also lists units its carrier projects run.
 * Only its pool carriers, which no project runs, accept a move.
 */
export function moveDestinationUnits(hierarchy?: CarrierHierarchyDto): CarrierUnitDto[] {
  if (!hierarchy) {
    return [];
  }

  return hierarchy.organisation ? hierarchy.units : hierarchy.units.filter((unit) => !unit.project);
}

/**
 * The group to read the default login method from. `carriers.get` answers for an operating unit
 * only and refuses a container, so where the container is what gets submitted the unit the API
 * would resolve it to — the organisation's registration target — is asked instead. A unit answers
 * with its own method or the one it inherits from the organisation above it.
 */
export function resolveLoginMethodGroupID(
  hierarchy: CarrierHierarchyDto | undefined,
  groupID?: string
): string | undefined {
  if (groupID && groupID === hierarchy?.organisation?.id) {
    return hierarchy.organisation.registrationTargetGroupID;
  }

  return groupID || undefined;
}

// The unit the courier joins, which is what `CreateCourierDto.groupID` wants. Picked from the
// hierarchy's units, so it is already a real group ID.
export function resolveCourierGroupID(form: Pick<CreateCourierFormState, 'groupID'>): string | undefined {
  return form.groupID || undefined;
}

export function mapCourierFormToCreateCourierDto(form: CreateCourierFormState, groupID: string): CreateCourierDto {
  return {
    groupID,
    firstName: form.firstName,
    ..._.omitBy(
      {
        lastName: form.lastName,
        mobile: form.mobile,
        externalID: form.externalID,
        username: form.username,
        accessCode: form.accessCode
      },
      (v) => v === '' || v === undefined
    )
  };
}

/** Seeds the edit form from the courier's current record, access code included — it is returned. */
export function mapCourierToForm(courier: CourierDto): CourierFormState {
  return {
    firstName: courier.firstName,
    lastName: courier.lastName || '',
    mobile: courier.mobile || '',
    externalID: courier.externalID || '',
    username: courier.username || '',
    accessCode: courier.accessCode || ''
  };
}

/** The credential fields each Login details tab is responsible for. */
const TAB_CREDENTIALS: Record<LoginTab, ('mobile' | 'externalID' | 'username' | 'accessCode')[]> = {
  mobile: ['mobile'],
  externalID: ['externalID'],
  // Both halves: the API counts neither a username nor a code on its own as a way to sign in.
  accessCode: ['username', 'accessCode']
};

/**
 * An edit sends only what changed. A field the operator emptied is sent as `null` to clear it,
 * whereas one that was already empty is omitted — the API treats an absent field as unchanged, so
 * sending `null` for a field that never had a value would be a pointless write.
 *
 * Moving the Login details toggle counts as a change, since choosing a credential the courier
 * already holds is otherwise an empty edit that closes the modal without saving. The toggle itself
 * is never sent — `EditCourierDto` has no field for it — so its tab's credential goes instead.
 */
export function mapCourierFormToEditDto(
  form: CourierFormState,
  courier: CourierDto,
  activeLoginTab?: LoginTab
): EditCourierDto {
  const changed: EditCourierDto = {};

  if (form.firstName !== courier.firstName) {
    changed.firstName = form.firstName;
  }

  assignOptional(changed, 'lastName', form.lastName, courier.lastName);
  assignOptional(changed, 'mobile', form.mobile, courier.mobile);
  assignOptional(changed, 'externalID', form.externalID, courier.externalID);
  assignOptional(changed, 'username', form.username, courier.username);
  // The access code is returned, so it seeds the field and round-trips like any other value: an
  // untouched field matches the record and sends nothing, an emptied one clears it.
  assignOptional(changed, 'accessCode', form.accessCode, courier.accessCode);

  if (activeLoginTab && activeLoginTab !== inferLoginTab(courier)) {
    for (const field of TAB_CREDENTIALS[activeLoginTab]) {
      const value = form[field];
      if (value) {
        changed[field] = value;
      }
    }
  }

  return changed;
}

function assignOptional(
  dto: EditCourierDto,
  field: 'lastName' | 'mobile' | 'externalID' | 'username' | 'accessCode',
  value: string | undefined,
  current: string | undefined
) {
  const next = value || undefined;
  if (next === (current || undefined)) {
    return;
  }
  // `null` is how the API is told to clear a field; the SDK types these as optional strings, so
  // the cast is what lets an intentional clear through.
  dto[field] = (next ?? null) as unknown as string;
}

/** The approval decision. Its own modal: the general edit refuses it, and it moves the status too. */
export interface CourierApprovalFormState {
  approvalStatus: string;
  reason?: string;
}

/**
 * The two decisions an operator may make. Pending and incomplete are not offered — a courier arrives
 * in them by registering, and only the rejected role survives the courier app re-reading AC.
 */
export const courierApprovalOptions: { label: string; value: string }[] = [
  { label: 'Approved', value: KnownCourierApprovalStatus.Approved },
  { label: 'Rejected', value: KnownCourierApprovalStatus.Rejected }
];

/** What the API accepts, so the field is held to it before a request is spent. */
export const APPROVAL_REASON_MAX_LENGTH = 1000;

export function makeCourierApprovalFormConfig(): FormConfigBase<CourierApprovalFormState> {
  return {
    approvalStatus: {
      name: 'approvalStatus',
      label: 'Approval status',
      required: true
    },
    // `reason` is the API's field name; operators know it as notes, so only the label differs.
    reason: {
      name: 'reason',
      label: 'Notes',
      required: false,
      validate: (reason) =>
        !reason ||
        reason.length <= APPROVAL_REASON_MAX_LENGTH ||
        `The notes cannot be longer than ${APPROVAL_REASON_MAX_LENGTH} characters.`
    }
  };
}

/**
 * The decision the courier is already in, or nothing when none has been made. Undecided is what lets
 * an approval register as a change worth sending.
 */
export function courierApprovalStatus(courier: Pick<CourierDto, 'accessLevel'>): string | undefined {
  switch (courier.accessLevel) {
    case KnownCourierAppRoles.CourierActive:
      return KnownCourierApprovalStatus.Approved;
    case KnownCourierAppRoles.CourierRejected:
      return KnownCourierApprovalStatus.Rejected;
    default:
      return undefined;
  }
}

/** A rejected courier is inactive, but so is a suspended one — only the access level tells them apart. */
export function isCourierRejected(courier: Pick<CourierDto, 'accessLevel'>): boolean {
  return courier.accessLevel === KnownCourierAppRoles.CourierRejected;
}

/** Opens on the decision the courier is in, or Approved when undecided — the usual outcome. */
export function mapCourierToApprovalForm(
  courier: Pick<CourierDto, 'accessLevel'>,
  /** Chosen on the way in — the badge's menu opens the modal on the decision that was picked. */
  preselected?: string
): CourierApprovalFormState {
  return {
    approvalStatus: preselected ?? courierApprovalStatus(courier) ?? KnownCourierApprovalStatus.Approved,
    // Never seeded from the last decision: notes belong to the decision being made.
    reason: ''
  };
}

/**
 * The decision to send, or nothing when unchanged. Re-sending is not harmless: approving sets the
 * status to active too, reinstating a courier deactivated since.
 */
export function mapCourierApprovalFormToDto(
  form: CourierApprovalFormState,
  courier: Pick<CourierDto, 'accessLevel'>
): CourierApprovalDto | undefined {
  if (!form.approvalStatus || form.approvalStatus === courierApprovalStatus(courier)) {
    return undefined;
  }

  const reason = form.reason?.trim();

  return {
    approvalStatus: form.approvalStatus,
    ...(reason ? { reason } : {})
  };
}

/** What the badge shows: their status, or the rejection — a rejected courier is inactive too. */
export function courierBadgeStatus(courier: Pick<CourierDto, 'status' | 'accessLevel'>): string | undefined {
  return isCourierRejected(courier) ? REJECTED_BADGE_STATUS : courier.status;
}

/**
 * The decisions the courier is not already in — both when undecided, the reversal otherwise. A
 * deleted or incomplete courier is offered none, as the endpoint refuses them.
 */
export function courierApprovalDecisions(courier: Pick<CourierDto, 'status' | 'accessLevel'>): string[] {
  if (courier.status === KnownUserStatus.Deleted || isCourierIncomplete(courier)) {
    return [];
  }

  const current = courierApprovalStatus(courier);

  return [KnownCourierApprovalStatus.Approved, KnownCourierApprovalStatus.Rejected].filter(
    (decision) => decision !== current
  );
}

/** What each decision reads as in the badge's menu. */
export const courierApprovalDecisionLabels: Record<string, string> = {
  [KnownCourierApprovalStatus.Approved]: 'Set as Approved',
  [KnownCourierApprovalStatus.Rejected]: 'Set as Rejected'
};
